import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useBusinessProfileData } from '../../hooks/useBusinessProfileData';
import { useTerms } from '../../contexts/TermsContext';
import { useBusinessProfile } from '../../contexts/BusinessProfileContext';
import { commissionService } from '../../services/commissionService';
import BusinessProfileForm from './BusinessProfileForm';
import FormSection from './FormSection';
import { TermsModal } from '../common/TermsModal';
import { FacilitiesSection } from '../Facilities/FacilitiesSection';
import { KSeFSection } from '../KSeF/KSeFSection';
import { ConsolidatedInvoicingToggle } from '../Invoicing/ConsolidatedInvoicingToggle';
import { TrainersSection } from '../Trainers/TrainersSection';
import { AgentsSection } from '../Agents/AgentsSection';
import type { TermsDocumentStatus } from '../../types/terms';
import type { CommissionStatus } from '../../types/commission';

// "Konfiguracja" tab — everything set up once and revisited rarely, in two
// groups: "Firma" (profile incl. TPay, consent documents, commission
// agreement) and "Konfiguracja" (facilities, KSeF, trainers, agents, widget).
// Day-to-day work (invoicing, reports, services) lives in OperationsPage.

// Group label — eyebrow style from the design system; tokens remap under .dark.
function GroupHeading({ children }: { children: string }) {
  return (
    <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-ink-muted pb-2 border-b border-border-subtle m-0">
      {children}
    </h2>
  );
}

export default function ConfigurationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { showToast } = useToast();
  const {
    isLoading,
    isSaving,
    profile,
    fullProfile,
    isNewProfile,
    legalForms,
    categories,
    loadError,
    loadData,
    handleSave,
    handleAvatarUpload,
    handleCopyBookingLink,
  } = useBusinessProfileData();
  const { documents } = useTerms();
  const { refetch: refetchSharedProfile } = useBusinessProfile();

  const [collapsedSections, setCollapsedSections] = useState({
    basicInfo: true,
    documents: true,
    commission: true,
    facilities: true,
    ksef: true,
    trainers: true,
    agents: true,
  });

  const toggleSection = (section: keyof typeof collapsedSections) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const [openDocument, setOpenDocument] = useState<TermsDocumentStatus | null>(null);

  // SW-98: commission status + the "request a preferential agreement" lead flow.
  const [commission, setCommission] = useState<CommissionStatus | null>(null);
  const [isRequestingCommission, setIsRequestingCommission] = useState(false);
  const [showCommissionConfirm, setShowCommissionConfirm] = useState(false);

  useEffect(() => {
    if (!token) return;
    commissionService.getStatus(token)
      .then(setCommission)
      .catch((err) => console.error('Failed to load commission status:', err));
  }, [token]);

  const handleRequestCommissionAgreement = async () => {
    if (!token) return;
    setIsRequestingCommission(true);
    try {
      const updated = await commissionService.requestAgreement(token);
      setCommission(updated);
      setShowCommissionConfirm(false);
      showToast('success', t('settingsPage.commissionRequestSuccess', 'Zapytanie zostało wysłane'));
    } catch (err) {
      console.error('Failed to request commission agreement:', err);
      showToast('error', t('settingsPage.commissionRequestError', 'Nie udało się wysłać zapytania'));
    } finally {
      setIsRequestingCommission(false);
    }
  };

  const commissionStatusLabel = (status: CommissionStatus['status']) => {
    switch (status) {
      case 'Active':
        return t('settingsPage.commissionStatusActive', 'Aktywna');
      case 'RequestSent':
        return t('settingsPage.commissionStatusRequestSent', 'Zapytanie wysłane');
      case 'Expired':
        return t('settingsPage.commissionStatusExpired', 'Wygasła');
      default:
        return t('settingsPage.commissionStatusNone', 'Brak');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="h-8 bg-surface-card rounded w-64 animate-pulse mb-2" />
          <div className="h-5 bg-surface-app-elevated rounded w-96 animate-pulse" />
        </div>
        <div className="space-y-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-surface-app-elevated rounded-lg border border-border-strong p-4">
              <div className="h-6 bg-surface-card rounded w-48 animate-pulse mb-4" />
              <div className="grid grid-cols-2 gap-4">
                <div className="h-10 bg-surface-card rounded animate-pulse" />
                <div className="h-10 bg-surface-card rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-brand-warm/30 border border-brand-warm text-white px-6 py-8 rounded-lg text-center">
          <svg className="w-12 h-12 mx-auto mb-4 opacity-75" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-xl font-semibold mb-2">{t('common.error')}</h2>
          <p className="mb-4">{loadError}</p>
          <button
            onClick={loadData}
            className="px-6 py-2 bg-brand-warm hover:bg-brand-warm-deep text-white rounded-lg transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-ink-primary">
            {t('navigation.configuration')}
          </h1>
          <p className="text-ink-secondary mt-1">
            {isNewProfile
              ? t('businessProfile.createDescription')
              : t('businessProfile.editDescription')}
          </p>
        </div>
        {!isNewProfile && profile?.id && (
          <button
            type="button"
            onClick={handleCopyBookingLink}
            title={t('businessProfile.copyLinkTooltip')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors text-sm font-medium flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {t('businessProfile.copyLink')}
          </button>
        )}
      </div>

      <div className="space-y-8">
        <section className="space-y-4">
          <GroupHeading>{t('configurationPage.groupBusiness', 'Firma')}</GroupHeading>

          {/* Basic Info Section */}
          <FormSection
            title={t('businessProfile.sections.basicInfo')}
            isOpen={!collapsedSections.basicInfo}
            onToggle={() => toggleSection('basicInfo')}
            variant="dark"
          >
            {profile && (
              <BusinessProfileForm
                initialData={profile}
                legalForms={legalForms}
                categories={categories}
                onSave={handleSave}
                onAvatarUpload={handleAvatarUpload}
                isSaving={isSaving}
                isNewProfile={isNewProfile}
              />
            )}
          </FormSection>

          {/* SW-91/SW-99: read-only review of the consent-gate documents — the
              gate already blocks the portal until everything here is accepted,
              so this is "one acceptance state, two entry points," not a
              second accept flow. */}
          {!isNewProfile && profile?.id && (
            <FormSection
              title={t('settingsPage.documentsSection', 'Dokumenty i zgody')}
              isOpen={!collapsedSections.documents}
              onToggle={() => toggleSection('documents')}
            >
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.key}
                    className="flex items-center justify-between gap-3 p-3 bg-surface-card border border-border-subtle rounded-lg"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-primary truncate">{doc.title}</p>
                      <p className="text-xs text-ink-secondary">
                        {t('settingsPage.documentVersion', 'Wersja')} {doc.version}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full ${
                          doc.accepted
                            ? 'bg-brand-confirm/15 text-brand-confirm-deep'
                            : 'bg-brand-warm/15 text-brand-warm-deep'
                        }`}
                      >
                        {doc.accepted
                          ? t('settingsPage.documentAccepted', 'Zaakceptowano')
                          : t('settingsPage.documentPending', 'Oczekuje')}
                      </span>
                      <button
                        type="button"
                        onClick={() => setOpenDocument(doc)}
                        className="text-sm text-brand-primary hover:text-brand-primary-hover underline"
                      >
                        {t('settingsPage.documentRead', 'Przeczytaj')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </FormSection>
          )}

          {/* SW-98: commission agreement request + read-only status. No rate
              input here by design — rate-setting is staff-only (packages/admin). */}
          {!isNewProfile && profile?.id && commission && (
            <FormSection
              title={t('settingsPage.commissionSection', 'Umowa czasowa z lepszą prowizją')}
              isOpen={!collapsedSections.commission}
              onToggle={() => toggleSection('commission')}
            >
              <div className="p-3 bg-surface-card border border-border-subtle rounded-lg space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-ink-secondary">{t('settingsPage.commissionStatusLabel', 'Status')}</span>
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full ${
                      commission.status === 'Active'
                        ? 'bg-brand-confirm/15 text-brand-confirm-deep'
                        : commission.status === 'RequestSent'
                        ? 'bg-brand-primary/15 text-brand-primary'
                        : commission.status === 'Expired'
                        ? 'bg-brand-warm/15 text-brand-warm-deep'
                        : 'bg-border-subtle text-ink-secondary'
                    }`}
                  >
                    {commissionStatusLabel(commission.status)}
                  </span>
                </div>

                {(commission.status === 'Active' || commission.status === 'Expired') && (
                  <div className="text-sm text-ink-primary">
                    <p>
                      {t('settingsPage.commissionRateLabel', 'Stawka')}: <strong>{commission.ratePercent}%</strong>
                      {commission.effectiveFrom && (
                        <span className="text-ink-secondary">
                          {' '}({commission.effectiveFrom.slice(0, 10)}
                          {commission.effectiveTo ? ` – ${commission.effectiveTo.slice(0, 10)}` : ''})
                        </span>
                      )}
                    </p>
                    {commission.signedContractUrl && (
                      <a
                        href={commission.signedContractUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary hover:text-brand-primary-hover underline text-sm"
                      >
                        {t('settingsPage.commissionContractLink', 'Zobacz podpisaną umowę')}
                      </a>
                    )}
                  </div>
                )}

                {(commission.status === 'None' || commission.status === 'Expired') && (
                  <div>
                    {!showCommissionConfirm ? (
                      <button
                        type="button"
                        onClick={() => setShowCommissionConfirm(true)}
                        className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors text-sm font-medium"
                      >
                        {t('settingsPage.commissionCtaButton', 'Podpisz umowę czasową z lepszą prowizją')}
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handleRequestCommissionAgreement}
                          disabled={isRequestingCommission}
                          className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 transition-colors text-sm font-medium"
                        >
                          {isRequestingCommission
                            ? t('settingsPage.commissionSubmitting', 'Wysyłanie...')
                            : t('settingsPage.commissionConfirmButton', 'Wyślij zapytanie')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCommissionConfirm(false)}
                          disabled={isRequestingCommission}
                          className="text-sm text-ink-secondary hover:text-ink-primary"
                        >
                          {t('common.cancel', 'Anuluj')}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {commission.status === 'RequestSent' && (
                  <p className="text-sm text-ink-secondary">
                    {t(
                      'settingsPage.commissionRequestSentHint',
                      'Skontaktujemy się z Tobą w sprawie warunków umowy.'
                    )}
                  </p>
                )}
              </div>
            </FormSection>
          )}
        </section>

        {!isNewProfile && profile?.id && (
          <section className="space-y-4">
            <GroupHeading>{t('configurationPage.groupSetup', 'Konfiguracja')}</GroupHeading>

            <FacilitiesSection
              businessProfileId={profile.id}
              isCollapsed={collapsedSections.facilities}
              onToggle={() => toggleSection('facilities')}
            />

            <KSeFSection
              businessProfile={fullProfile}
              isCollapsed={collapsedSections.ksef}
              onToggle={() => toggleSection('ksef')}
              onProfileUpdate={() => {
                loadData();
                // keeps the KSeF-expiry dot on the header gear in sync
                refetchSharedProfile();
              }}
            >
              {fullProfile && (
                <ConsolidatedInvoicingToggle businessProfile={fullProfile} onProfileUpdate={loadData} />
              )}
            </KSeFSection>

            <TrainersSection
              businessProfileId={profile.id}
              isCollapsed={collapsedSections.trainers}
              onToggle={() => toggleSection('trainers')}
            />

            <AgentsSection
              businessProfileId={profile.id}
              isCollapsed={collapsedSections.agents}
              onToggle={() => toggleSection('agents')}
            />

            {/* Embed Widget — links to its own page so the editing surface
                isn't squeezed inside this already-busy view. */}
            <div className="bg-surface-card border border-border-subtle rounded-xl p-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="font-display text-base font-semibold text-ink-primary m-0">
                  {t('embedWidget.pageTitlePrefix')}{' '}
                  <span className="font-mono uppercase tracking-[0.12em] text-brand-primary text-sm align-middle">
                    Plugin Commerce
                  </span>
                </h2>
                <p className="text-ink-muted text-sm mt-1 mb-0">
                  {t('embedWidget.navCard.description')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/settings/widget')}
                className="px-4 py-2 bg-brand-primary text-white border-0 rounded-lg font-semibold cursor-pointer whitespace-nowrap"
              >
                {t('embedWidget.navCard.openButton')}
              </button>
            </div>
          </section>
        )}
      </div>

      {/* Version stamp */}
      <div className="mt-6 text-right text-[10px] text-ink-muted tracking-wider font-mono select-all">
        v{__APP_VERSION__}
      </div>

      {openDocument && (
        <TermsModal
          isOpen={!!openDocument}
          onClose={() => setOpenDocument(null)}
          type="dataProcessing"
          titleOverride={openDocument.title}
          contentOverride={openDocument.content}
        />
      )}
    </div>
  );
}
